import type { FC } from 'react';
import { Icon, SaleTag, Text } from '@cencosud-cencommerce/paris-uikit';

import { ESellerType } from '@/Application/Containers/DeliveryCard/interfaces';
import type { DeliveryGroup } from '@/modules/shippingMethod/domain/ShippingMethod';
import { TAB_PICKUP } from '@/Pages/BuyNowPage/PaymentAndShipping/constants';
import type { ShippingTabNavigationProps } from '@/Pages/BuyNowPage/PaymentAndShipping/interfaces';
import ShippingTabNavigation from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingMethods/ShippingTabNavigation';

const LABEL_DISPATCH = 'Entrega';
const LABEL_SEND_BY = 'Enviado por';

// Constants from DeliveryCard logic
const COLLECT = 'COLLECT';
const WAREHOUSE = 'WAREHOUSE';
const PROVIDER = 'PROVIDER';
const SHIP_FROM_STORE = 'SHIP_FROM_STORE';

// Default fallbacks
const LABEL_SENT_BY_PARIS = 'Paris';

interface ShippingMethodHeaderProps extends ShippingTabNavigationProps {
  deliveryGroup?: DeliveryGroup;
}

export const ShippingMethodHeader: FC<ShippingMethodHeaderProps> = (props) => {
  const { deliveryGroup, ...navigationProps } = props;

  // Helper function to format title
  const formatTitle = (str: string): string =>
    str
      .toLowerCase()
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // Calculate "Sent By" logic - same as DeliveryCard
  const calculateSentBy = (): string => {
    if (!deliveryGroup) return LABEL_SENT_BY_PARIS;

    const selectedShippingOption = deliveryGroup.shippingOptions?.find(
      (option) => option.selected
    );
    const selectedPickupOption = deliveryGroup.pickupOptions?.find(
      (option) => option.selected
    );

    // If we're in pickup tab but no pickup option is selected, don't show "Sent By"
    if (navigationProps.tabActive === TAB_PICKUP && !selectedPickupOption) {
      return '';
    }

    // If we're in pickup tab and have a pickup option selected, don't show "Sent By"
    if (selectedPickupOption) {
      return '';
    }

    if (!selectedShippingOption) return LABEL_SENT_BY_PARIS;

    const productList = deliveryGroup.productList || [];
    const firstProduct = productList[0];

    const rawStoreName = firstProduct?.saleTagProps?.storeName;
    const sellerType =
      rawStoreName && rawStoreName.toLowerCase() !== 'paris'
        ? ESellerType['3P']
        : ESellerType['1P'];
    const storeName = formatTitle(rawStoreName ?? '');
    const classificationGroupMeta = deliveryGroup.classificationGroupMeta;
    const originNodeName = formatTitle(
      classificationGroupMeta?.originNodeNameIndex ?? ''
    );
    const originNodeType = selectedShippingOption.originNodeType;
    const originNodeAdmin = formatTitle(
      selectedShippingOption.originNodeAdmin ?? ''
    );
    const originNodeCode = selectedShippingOption.originNodeCode;
    const isWarehouse = originNodeType === WAREHOUSE;
    const isProvider = originNodeType === PROVIDER;
    const isShipFromStore =
      selectedShippingOption.shippingCapability === SHIP_FROM_STORE;

    // Logs for tracking missing PES data
    if (!originNodeName && !originNodeAdmin && !originNodeCode) {
      console.log('🚨 [FastCheckout] Datos PES faltantes:', {
        deliveryGroupId: deliveryGroup.id,
        originNodeNameIndex: classificationGroupMeta?.originNodeNameIndex,
        originNodeAdmin: selectedShippingOption.originNodeAdmin,
        originNodeCode: selectedShippingOption.originNodeCode,
        shippingCapability: selectedShippingOption.shippingCapability,
        originNodeType: selectedShippingOption.originNodeType
      });
    }

    // If it's COLLECT (pickup), don't show "Sent By"
    if (selectedShippingOption.shippingType === COLLECT) {
      return '';
    }

    // LÓGICA PRINCIPAL: Primero diferenciamos 1P vs 3P, luego origen (bodega vs tienda)

    // 1P Logic (Productos Paris)
    if (sellerType === ESellerType['1P']) {
      // Si es de bodega → "Paris"
      if (isWarehouse || isProvider) {
        return LABEL_SENT_BY_PARIS;
      } else if (isShipFromStore && originNodeName) {
        // Si es de tienda → "Paris & [Nombre tienda]"
        return originNodeName;
      }
      // Fallback para 1P
      return LABEL_SENT_BY_PARIS;
    }

    // 3P Logic (Productos Marketplace)
    if (sellerType === ESellerType['3P']) {
      // Si es de bodega → "[Nombre seller]"
      if (isWarehouse || isProvider) {
        return storeName;
      } else if (isShipFromStore && originNodeName) {
        // Si es de tienda → "[Nombre seller] & [Nombre tienda]"
        return `${storeName} & ${originNodeName}`;
      }
      // Fallback para 3P
      return storeName;
    }

    // Default fallback
    return LABEL_SENT_BY_PARIS;
  };

  const sentByStoreName = calculateSentBy();
  return (
    <header className="co-flex co-justify-between co-w-full tablet:co-p-4 co-border-b co-border-b-neutral-300 co-flex-col tablet:co-flex-row co-gap-2">
      <aside className="co-flex tablet:co-gap-2 co-items-center co-p-3 tablet:co-p-0 co-justify-between">
        <div className="co-flex co-gap-2 co-items-center">
          <div>
            <Icon name={'il-product'} className="co-size-8" />
          </div>
          <Text variant="subtitle2" weight={600}>
            {LABEL_DISPATCH}
          </Text>
          {sentByStoreName && (
            <SaleTag
              label={LABEL_SEND_BY}
              storeName={sentByStoreName}
              className="co-break-all"
            />
          )}
        </div>
      </aside>

      <ShippingTabNavigation {...navigationProps} />
    </header>
  );
};
